import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useState } from 'react';

import StatusTabs from '~components/Tabs/StatusTabs';
import { FeatureFlags } from '~constants/featureFlags';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import DiscardUnsavedChangesDialog from './DiscardUnsavedChangesDialog';
import ItemRulesTab from './ItemRulesTab';
import ItemsTab from './ItemsTab';

const I18N_PREFIX = 'administration.integration.rutter.quick_books_online';

enum ItemsSubTab {
  DirectMapping = 'direct_mapping',
  ItemRules = 'item_rules',
}

const SUB_TABS = [
  {
    value: ItemsSubTab.DirectMapping,
    label: t(`${I18N_PREFIX}.item_rules.tabs.direct_mapping`),
  },
  { value: ItemsSubTab.ItemRules, label: t(`${I18N_PREFIX}.item_rules.tabs.item_rules`) },
];

interface ItemsSectionProps {
  onItemRulesDirtyChange: (isDirty: boolean) => void;
}

export default function ItemsSection({ onItemRulesDirtyChange }: ItemsSectionProps) {
  const isItemRulesEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.qbItemMatchLlm,
  });
  const [subTab, setSubTab] = useState(ItemsSubTab.DirectMapping);
  const [hasUnsavedRules, setHasUnsavedRules] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  const handleDirtyChange = (isDirty: boolean) => {
    setHasUnsavedRules(isDirty);
    onItemRulesDirtyChange(isDirty);
  };

  const requestDirectMapping = () => {
    if (hasUnsavedRules) {
      setIsDiscardDialogOpen(true);

      return;
    }

    setSubTab(ItemsSubTab.DirectMapping);
  };

  const confirmDiscard = () => {
    setIsDiscardDialogOpen(false);
    handleDirtyChange(false);
    setSubTab(ItemsSubTab.DirectMapping);
  };

  if (!isItemRulesEnabled) {
    return (
      <>
        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
          {t(`${I18N_PREFIX}.tread_first.banners.items`)}
        </Alert>
        <ItemsTab />
      </>
    );
  }

  const isItemRulesSelected = subTab === ItemsSubTab.ItemRules;

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <StatusTabs
          onChange={(nextSubTab) => {
            if (nextSubTab === ItemsSubTab.DirectMapping) {
              requestDirectMapping();
            } else {
              setSubTab(nextSubTab);
            }
          }}
          selectedTab={subTab}
          tabs={SUB_TABS}
        />
      </Box>

      <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
        {isItemRulesSelected
          ? t(`${I18N_PREFIX}.item_rules.banner`)
          : t(`${I18N_PREFIX}.tread_first.banners.items`)}
      </Alert>

      {isItemRulesSelected ? (
        <ItemRulesTab
          onDirtyChange={handleDirtyChange}
          onOpenDirectMapping={requestDirectMapping}
        />
      ) : (
        <Box>
          <ItemsTab />
        </Box>
      )}

      <DiscardUnsavedChangesDialog
        isOpen={isDiscardDialogOpen}
        onClose={() => setIsDiscardDialogOpen(false)}
        onConfirm={confirmDiscard}
      />
    </>
  );
}
